import {
  B2BConfigurationProductRelationType,
  DEFAULT_B2B_CONFIGURATION,
  type B2BConfiguration,
  type B2BConfigurationCatalog,
  type B2BConfigurationLandingTemplateItem,
  type B2BConfigurationProductTemplateItem,
  type B2BConfigurationThemeworldTemplateItem,
} from '@@fire-b2b/types/ClientConfiguration'
import type { HexColorString } from '@@fire-base/types/utils/ColorString'
import { defineStore } from 'pinia'

export type TagStyle = {
  productTagId: number
  backgroundColor: HexColorString
  backgroundColorHover?: HexColorString
  lineColor: HexColorString
}

export const useB2BConfigStore = defineStore('b2bConfig', () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  const { selectedBrand } = storeToRefs(useBrandsStore())

  const b2bConfig = computed<B2BConfiguration | null>(() => {
    return selectedBrand.value?.b2bConfiguration ?? null
  })

  const hasProductLineSplit = computed<boolean>(() => {
    const brand = selectedBrand.value
    if (brand) {
      return brand.preOrderSplitProductline || brand.reOrderSplitProductline
    }
    return false
  })

  const hasSeasonSplit = computed<boolean>(() => {
    const brand = selectedBrand.value
    if (brand) {
      return brand.preOrderSplitSeason || brand.reOrderSplitSeason
    }
    return false
  })

  const productRelations = computed(() => {
    const relatedConfig = b2bConfig.value?.productRelations

    return relatedConfig?.map((r) => {
      let id = r.id

      switch (r.type) {
        case B2BConfigurationProductRelationType.COLOR:
          id = 'color'
          break
        case B2BConfigurationProductRelationType.THEMEWORLD:
          id = 'themeworld'
          break
        case B2BConfigurationProductRelationType.LOOK:
          id = 'look'
          break
      }

      return {
        ...r,
        id,
      }
    })
  })

  const productInfoTabs = computed(() => {
    return b2bConfig.value?.productInfoTabs || []
  })

  const capsuleConfig = computed(() => {
    const capsule = b2bConfig.value?.themeworld
    return {
      mainTitle: resolveLocalizedString(capsule?.mainTitle),
      showVideos: capsule?.showVideo ?? false,
      categoryTitleUppercase: capsule?.categoryTitleUppercase ?? false,
    }
  })

  const lookConfig = computed(() => {
    const look = b2bConfig.value?.look
    return {
      mainTitle: resolveLocalizedString(look?.mainTitle),
      showTitleInSlider: look?.showTitleInSlider ?? false,
      useCustomTitle: look?.useCustomTitle ?? false,
      showVideos: look?.showVideo ?? false,
      imageGallery: look?.imageGallery ?? [],
      categoryTitleUppercase: look?.categoryTitleUppercase ?? false,
    }
  })

  const productConfig = computed(() => {
    const product = b2bConfig.value?.product
    return {
      showVideos: product?.showVideo ?? false,
      allowWishlist: product?.allowWishlist ?? false,
      showDescription: product?.showDescription ?? false,
      bulletpoints: product?.bulletpoints ?? [],
      tagStyle:
        product?.tagStyle?.map<TagStyle>((s) => {
          return {
            productTagId: s.id,
            backgroundColor: s.color1,
            backgroundColorHover: s.color1Hover,
            lineColor: s.color2,
          }
        }) ?? [],
    }
  })

  const catalogConfig = computed<Required<B2BConfigurationCatalog>>(() => {
    return {
      useTemplate: b2bConfig.value?.catalog?.useTemplate ?? false,
      hasOnlyMainColor: b2bConfig.value?.catalog?.hasOnlyMainColor ?? false,
      hasListView: b2bConfig.value?.catalog?.hasListView ?? false,
      hasGridView: b2bConfig.value?.catalog?.hasGridView ?? false,
      hasSizes: b2bConfig.value?.catalog?.hasSizes ?? false,
      useListView: b2bConfig.value?.catalog?.useListView ?? false,
      useListViewMobile: b2bConfig.value?.catalog?.useListViewMobile ?? false,
      useOnlyMainColor: b2bConfig.value?.catalog?.useOnlyMainColor ?? false,
    }
  })

  const DEFAULT_PRODUCT_TEMPLATE: B2BConfigurationProductTemplateItem[] = [
    'details',
    'themeworlds',
    'looks',
    'relatedProducts',
  ]

  const productTemplate = computed<B2BConfigurationProductTemplateItem[]>(() => {
    const product = b2bConfig.value?.product
    if (product?.useTemplate && product.template?.length) {
      return product.template
    }
    return DEFAULT_PRODUCT_TEMPLATE
  })

  const DEFAULT_THEMEWORLD_TEMPLATE: B2BConfigurationThemeworldTemplateItem[] = [
    'hero',
    'keyPieces',
    'story',
    'products',
    'relatedThemeworlds',
  ]

  const themeworldTemplate = computed<B2BConfigurationThemeworldTemplateItem[]>(() => {
    const themeworld = b2bConfig.value?.themeworld
    if (themeworld?.useTemplate && themeworld.template?.length) {
      return themeworld.template
    }
    return DEFAULT_THEMEWORLD_TEMPLATE
  })

  const DEFAULT_LANDING_TEMPLATE: B2BConfigurationLandingTemplateItem[] = ['heroBanner', 'themeworlds', 'news', 'looks']

  const landingPreOrderTemplate = computed<B2BConfigurationLandingTemplateItem[]>(() => {
    const landing = b2bConfig.value?.landing
    if (landing?.useTemplate && landing.preOrderTemplate?.length) {
      return landing.preOrderTemplate
    }
    return DEFAULT_LANDING_TEMPLATE
  })

  const landingReOrderTemplate = computed<B2BConfigurationLandingTemplateItem[]>(() => {
    const landing = b2bConfig.value?.landing
    if (landing?.useTemplate && landing.reOrderTemplate?.length) {
      return landing.reOrderTemplate
    }
    return DEFAULT_LANDING_TEMPLATE
  })

  const blogConfig = computed(() => {
    const blog = b2bConfig.value?.blog
    return {
      useModule: blog?.useModule !== false,
      useTemplate: blog?.useTemplate ?? false,
    }
  })

  const elementsConfig = computed(() => ({
    lookCategoryItem: {
      showNoOfItems: b2bConfig.value?.elements?.lookCategoryItem?.showNoOfItems ?? true,
    },
  }))

  const openOrderConfig = computed(() => b2bConfig.value?.openOrder ?? DEFAULT_B2B_CONFIGURATION.openOrder)

  return {
    b2bConfig,
    blogConfig,
    catalogConfig,
    productRelations,
    isLoading,
    capsuleConfig,
    landingPreOrderTemplate,
    landingReOrderTemplate,
    lookConfig,
    productConfig,
    productTemplate,
    themeworldTemplate,
    hasProductLineSplit,
    hasSeasonSplit,
    productInfoTabs,
    elementsConfig,
    error,
    openOrderConfig,
  }
})
